import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users, HardHat, CalendarDays, CalendarRange, Clock, BarChart3, RefreshCw, Phone, CheckCircle2, Loader2, Package, Wrench, Undo2, PenLine, FileDown, ChevronLeft, ChevronRight, Search, X, ShieldCheck, ShieldAlert, CalendarPlus, MapPin, Printer, Copy, List, Tag, BookUser, StickyNote, LifeBuoy, Eye, EyeOff, ClipboardList, Link2, Receipt, AlertTriangle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, API } from "../lib/api";
import InstallerTaskModal from "../components/installers/InstallerTaskModal";
import InstallerStats from "../components/installers/InstallerStats";
import InstallerRubrica from "../components/installers/InstallerRubrica";
import InstallerAssistenze from "../components/installers/InstallerAssistenze";
import InstallerContestazioni from "../components/installers/InstallerContestazioni";
import InstallerAttivita, { ActivityModal } from "../components/installers/InstallerAttivita";
import InstallerPayslips from "../components/installers/InstallerPayslips";
import InstallerRecentPhotosBar from "../components/installers/InstallerRecentPhotosBar";

/** Pannello Installers — gestionale per l'app mobile operai (Archi-Hub Installers). */
const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  in_progress: "bg-sky-50 text-sky-800 border-sky-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const STATUS_LABEL = { pending: "Da fare", in_progress: "In corso", completed: "Completata" };
const STATUS_CHIP = { pending: "bg-white border-ink/15 text-ink", in_progress: "bg-sky-50 border-sky-300 text-sky-900", completed: "bg-emerald-50 border-emerald-300 text-emerald-900" };
const ABS_LABEL = { malattia: "Malattia", permesso: "Permesso", ferie: "Ferie" };
const ABS_STYLE = {
  malattia: "bg-rose-50 text-rose-700 border-rose-200",
  permesso: "bg-violet-50 text-violet-700 border-violet-200",
  ferie: "bg-sky-50 text-sky-700 border-sky-200",
};

function fmtMinutes(min) {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtTs(iso) {
  try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }); } catch { return iso; }
}

function mondayOf(date) {
  const x = new Date(date);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isoDay(d) { return d.toLocaleDateString("sv-SE"); }
// Lavoro su più giorni: estremi e durata (da scheduled_date a scheduled_end_date, inclusi)
function taskSpan(t) {
  const start = t.scheduled_date || "";
  const end = (t.scheduled_end_date && t.scheduled_end_date > start) ? t.scheduled_end_date : start;
  if (!start) return { start, end, total: 1 };
  const total = Math.round((new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000) + 1;
  return { start, end, total: total > 0 ? total : 1 };
}
function dayInSpan(t, key) {
  const { start, total } = taskSpan(t);
  if (total <= 1 || !start) return null;
  const idx = Math.round((new Date(`${key}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000) + 1;
  return { idx, total };
}

function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
}
function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const emptyWorker = { email: "", password: "", name: "", phone: "", qualifica: "operaio" };
const NEW_TYPES = [
  { type: "delivery", label: "Consegna", Icon: Package },
  { type: "assistance", label: "Assistenza", Icon: LifeBuoy },
  { type: "activity", label: "Attività", Icon: ClipboardList },
];

export default function AdminInstallers() {
  const [sub, setSub] = useState("dashboard");
  const [operaiTab, setOperaiTab] = useState("operai"); // sotto-tab dentro "Operai": operai | bustepaga | sedi
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modalTask, setModalTask] = useState(undefined); // undefined=chiuso, null=nuova, obj=modifica
  const [dupFrom, setDupFrom] = useState(null); // consegna sorgente per duplicazione
  const [newTaskType, setNewTaskType] = useState("delivery"); // tipo preimpostato per nuova (delivery|assistance)
  const [assistReload, setAssistReload] = useState(0); // ricarica lista assistenze dopo salvataggio
  const [modalActivity, setModalActivity] = useState(undefined); // undefined=chiuso, null=nuova, obj=modifica
  const [actReload, setActReload] = useState(0); // ricarica lista attività dopo salvataggio
  const [viewMode, setViewMode] = useState("week"); // week | list
  const [wForm, setWForm] = useState(emptyWorker);
  const [wEdit, setWEdit] = useState(null); // {id, name, email, phone, password}
  const [wDelete, setWDelete] = useState(null); // operaio da eliminare (doppia conferma)
  const [wDelStep, setWDelStep] = useState(1);
  const [wDelText, setWDelText] = useState("");
  const [newTaskDate, setNewTaskDate] = useState(null); // data preimpostata da cella griglia
  const [newActivityDate, setNewActivityDate] = useState(null); // data preimpostata per nuova attività
  const [typeMenu, setTypeMenu] = useState(null); // "main" | dateKey | null → menu scelta tipo (Consegna/Assistenza/Attività)
  const [reschedDates, setReschedDates] = useState({}); // {taskId: "YYYY-MM-DD"} riprogrammazione
  const [dragOverDay, setDragOverDay] = useState(null); // cella evidenziata durante il drag

  const [tsMonth, setTsMonth] = useState(() => new Date().toLocaleDateString("sv-SE").slice(0, 7));
  const [tsWorker, setTsWorker] = useState("");
  const [tsEdit, setTsEdit] = useState(null); // {worker_id, worker_name, date, in_time, out_time, isNew}
  const [entryModal, setEntryModal] = useState(null); // singola timbratura: {id, worker_id, worker_name, date, type, time, note, isNew}
  const [timesheets, setTimesheets] = useState(null);
  const [supplies, setSupplies] = useState(null);
  const [supplyFilter, setSupplyFilter] = useState("");
  const [sForm, setSForm] = useState({ item: "", qty: "", note: "" });
  const [gcal, setGcal] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteForm, setSiteForm] = useState({ name: "", address: "", lat: "", lng: "" });

  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get("/installers/admin/sites");
      setSites(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento sedi");
    }
  }, []);

  async function createSite(e) {
    e.preventDefault();
    try {
      await api.post("/installers/admin/sites", {
        name: siteForm.name,
        address: siteForm.address,
        lat: siteForm.lat !== "" ? Number(siteForm.lat) : null,
        lng: siteForm.lng !== "" ? Number(siteForm.lng) : null,
      });
      setSiteForm({ name: "", address: "", lat: "", lng: "" });
      toast.success("Sede creata");
      loadSites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione sede");
    }
  }

  async function deleteSite(s) {
    if (!window.confirm(`Eliminare la sede "${s.name}"? Il suo QR non funzionerà più.`)) return;
    try {
      await api.delete(`/installers/admin/sites/${s.id}`);
      toast.success("Sede eliminata");
      loadSites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione sede");
    }
  }

  function printSiteQr(s) {
    const svg = document.getElementById(`site-qr-${s.id}`)?.outerHTML || "";
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>QR ${s.name.replace(/</g, "")}</title></head>
<body style="display:flex;flex-direction:column;align-items:center;font-family:Helvetica,Arial,sans-serif;padding:48px">
<div style="font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#888">ARCHI-HUB · Timbratura</div>
<h1 style="margin:8px 0 4px;font-size:26px">${s.name.replace(/</g, "&lt;")}</h1>
<p style="margin:0 0 28px;color:#555">${(s.address || "").replace(/</g, "&lt;")}</p>
<div style="transform:scale(2);transform-origin:top center;margin-bottom:120px">${svg}</div>
<p style="color:#888;font-size:13px">Scansiona con l'app operai per timbrare entrata / uscita</p>
<script>window.onload = () => window.print()</` + `script></body></html>`);
    w.document.close();
  }

  const loadGcal = useCallback(async () => {
    try {
      const { data } = await api.get("/installers/admin/gcal-settings");
      setGcal(data);
    } catch { /* card nascosta se errore */ }
  }, []);

  async function saveGcal(patch) {
    try {
      const { data } = await api.put("/installers/admin/gcal-settings", { enabled: gcal.enabled, google_email: gcal.google_email, ...patch });
      setGcal(data);
      toast.success(data.enabled ? "Sync Google Calendar attiva" : "Sync Google Calendar disattivata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore impostazioni calendario");
    }
  }

  const loadSupplies = useCallback(async () => {
    try {
      const { data } = await api.get("/installers/supplies", { params: supplyFilter ? { status: supplyFilter } : {} });
      setSupplies(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento ferramenta");
    }
  }, [supplyFilter]);

  const loadBase = useCallback(async () => {
    try {
      const [s, w, r] = await Promise.all([
        api.get("/installers/admin/stats"),
        api.get("/installers/admin/workers"),
        api.get("/installers/admin/reports"),
      ]);
      setStats(s.data); setWorkers(w.data); setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento Installers");
    }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const params = { status: statusFilter };
      if (q.trim()) params.q = q.trim();
      else if (viewMode === "week") { params.date_from = isoDay(weekStart); params.date_to = isoDay(addDays(weekStart, 6)); }
      else if (viewMode === "incorso") { params.status = "in_progress"; }
      // Lista: tutte le consegne (passate + future), ordinate per data, pagina ferma in cima
      const { data } = await api.get("/installers/tasks", { params });
      setTasks(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento consegne");
    }
  }, [weekStart, statusFilter, q, viewMode]);

  const loadTimesheets = useCallback(async () => {
    try {
      const { data } = await api.get("/installers/admin/timesheets/month", { params: { month: tsMonth } });
      setTimesheets(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore timbrature");
    }
  }, [tsMonth]);

  // Assenze: l'admin può aggiungere/rimuovere assenze (malattia/ferie) per un operaio
  const [absAdd, setAbsAdd] = useState(null); // { workerId, workerName } | null
  const [absDate, setAbsDate] = useState("");
  const [absType, setAbsType] = useState("ferie");
  const [absBusy, setAbsBusy] = useState(false);
  // Richieste di assenza operai (approva/rifiuta/modifica/elimina + storico)
  const [assenze, setAssenze] = useState(null);          // admin: tutte le richieste
  const [assFilter, setAssFilter] = useState("pending");  // pending | storico
  const [pendingAbsCount, setPendingAbsCount] = useState(0);
  const [absEdit, setAbsEdit] = useState(null);           // modifica inline: { id, worker_name, type, date }
  const [absBusyId, setAbsBusyId] = useState("");         // id richiesta in lavorazione

  async function submitAbsence(e) {
    e.preventDefault();
    if (!absDate) { toast.error("Scegli la data dell'assenza"); return; }
    setAbsBusy(true);
    try {
      await api.post("/installers/assenze", { worker_id: absAdd.workerId, type: absType, date: absDate });
      toast.success(`Assenza registrata per ${absAdd.workerName}`);
      setAbsAdd(null);
      loadTimesheets();
      loadAssenze();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore registrazione assenza");
    }
    setAbsBusy(false);
  }
  async function deleteAbsence(absenceId, dateLabel) {
    if (!window.confirm(`Rimuovere l'assenza del ${dateLabel}?`)) return;
    try {
      await api.delete(`/installers/assenze/${absenceId}`);
      toast.success("Assenza rimossa");
      loadTimesheets();
      loadAssenze();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore rimozione assenza");
    }
  }

  const loadAssenze = useCallback(async () => {
    try {
      const { data } = await api.get("/installers/assenze");
      setAssenze(data);
      setPendingAbsCount(data.filter((a) => a.status === "pending").length);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento richieste assenza");
    }
  }, []);

  async function reviewAbsence(a, action) {
    setAbsBusyId(a.id);
    try {
      await api.post(`/installers/assenze/${a.id}/${action}`);
      toast.success(action === "approve" ? `Assenza approvata · ${a.worker_name}` : `Richiesta rifiutata · ${a.worker_name}`);
      loadAssenze();
      loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore gestione richiesta");
    }
    setAbsBusyId("");
  }

  async function deleteAbsenceReq(a) {
    if (!window.confirm(`Eliminare la richiesta di ${a.worker_name} (${ABS_LABEL[a.type] || a.type} · ${a.date})?`)) return;
    try {
      await api.delete(`/installers/assenze/${a.id}`);
      toast.success("Richiesta eliminata");
      loadAssenze();
      loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  }

  async function saveAbsEdit() {
    if (!absEdit.date) { toast.error("Scegli la data"); return; }
    setAbsBusyId(absEdit.id);
    try {
      await api.put(`/installers/assenze/${absEdit.id}`, { type: absEdit.type, date: absEdit.date });
      toast.success("Assenza aggiornata");
      setAbsEdit(null);
      loadAssenze();
      loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore modifica");
    }
    setAbsBusyId("");
  }

  useEffect(() => {
    const h = setTimeout(() => setQ(qInput), 350);
    return () => clearTimeout(h);
  }, [qInput]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadGcal(); }, [loadGcal]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { if (sub === "timbrature") loadTimesheets(); }, [sub, loadTimesheets]);
  useEffect(() => { if (sub === "operai" && operaiTab === "sedi") loadSites(); }, [sub, operaiTab, loadSites]);
  useEffect(() => { loadSupplies(); }, [loadSupplies]);
  useEffect(() => { loadAssenze(); }, [loadAssenze]);

  async function addSupply(e) {
    e.preventDefault();
    if (!sForm.item.trim()) return;
    try {
      await api.post("/installers/supplies", sForm);
      setSForm({ item: "", qty: "", note: "" });
      loadSupplies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  async function setSupplyStatus(s, status) {
    try {
      await api.patch(`/installers/supplies/${s.id}/status`, { status });
      loadSupplies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  async function deleteSupply(s) {
    if (!window.confirm(`Eliminare "${s.item}"?`)) return;
    try {
      await api.delete(`/installers/supplies/${s.id}`);
      loadSupplies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  async function createWorker(e) {
    e.preventDefault();
    try {
      await api.post("/installers/admin/workers", wForm);
      toast.success("Operaio creato");
      setWForm(emptyWorker);
      loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione operaio");
    }
  }

  async function deleteWorker() {
    try {
      await api.delete(`/installers/admin/workers/${wDelete.id}`);
      toast.success("Operaio eliminato");
      setWDelete(null);
      loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  async function saveWorkerEdit(e) {
    e.preventDefault();
    try {
      const payload = { name: wEdit.name, email: wEdit.email, phone: wEdit.phone, qualifica: wEdit.qualifica || "operaio" };
      if (wEdit.password) payload.password = wEdit.password;
      await api.patch(`/installers/admin/workers/${wEdit.id}`, payload);
      toast.success("Operaio aggiornato");
      setWEdit(null);
      loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento operaio");
    }
  }

  async function deleteTask(t) {
    const lbl = t.task_type === "assistance" ? "assistenza" : t.task_type === "activity" ? "attività" : "consegna";
    if (!window.confirm(`Eliminare ${lbl === "attività" ? "l'attività" : lbl === "assistenza" ? "l'assistenza" : "la consegna"} "${t.title}"? Verranno rimossi anche note, foto e allegati.`)) return;
    try {
      await api.delete(`/installers/tasks/${t.id}`);
      toast.success(`${lbl.charAt(0).toUpperCase()}${lbl.slice(1)} eliminata`);
      loadTasks(); loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  async function rescheduleTask(t) {
    const newDate = reschedDates[t.id];
    if (!newDate || newDate === t.scheduled_date) return;
    try {
      await api.put(`/installers/tasks/${t.id}`, { scheduled_date: newDate });
      toast.success(`Consegna riprogrammata al ${new Date(`${newDate}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}`);
      setReschedDates((p) => { const n = { ...p }; delete n[t.id]; return n; });
      loadTasks(); loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore riprogrammazione");
    }
  }

  async function moveTaskToDate(taskId, dateKey, sourceDay = null) {
    const t = tasks.find((x) => x.id === taskId);
    if (!t) return;
    const { start, end, total } = taskSpan(t);
    // Sposta mantenendo la durata: delta = dal giorno trascinato (sourceDay) al giorno di rilascio
    const fromDay = sourceDay || start;
    const delta = Math.round((new Date(`${dateKey}T00:00:00`) - new Date(`${fromDay}T00:00:00`)) / 86400000);
    if (!delta) return;
    const newStart = isoDay(addDays(new Date(`${start}T00:00:00`), delta));
    try {
      const body = { scheduled_date: newStart };
      if (total > 1 && end) body.scheduled_end_date = isoDay(addDays(new Date(`${end}T00:00:00`), delta));
      await api.put(`/installers/tasks/${taskId}`, body);
      const label = t.task_type === "assistance" ? "Assistenza" : t.task_type === "activity" ? "Attività" : "Consegna";
      toast.success(`${label} "${t.title}" spostata a ${new Date(`${newStart}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}`);
      loadTasks(); loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore spostamento");
    }
  }

  // Menu "+ Nuova": apre la modale giusta per tipo (consegna/assistenza/attività), con data preimpostata
  function chooseNewType(type, dateKey = null) {
    setTypeMenu(null);
    setDupFrom(null);
    if (type === "activity") {
      setNewActivityDate(dateKey);
      setModalActivity(null);
    } else {
      setNewTaskType(type);
      setNewTaskDate(dateKey);
      setModalTask(null);
    }
  }

  async function changeStatus(t, status) {
    try {
      await api.patch(`/installers/tasks/${t.id}/status`, { status });
      loadTasks(); loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore cambio stato");
    }
  }

  async function saveTsEdit() {
    if (!tsEdit.worker_id) { toast.error("Seleziona l'operaio"); return; }
    if (!tsEdit.date || !tsEdit.in_time) { toast.error("Compila data e entrata"); return; }
    try {
      await api.put("/installers/admin/timbrature/day", {
        worker_id: tsEdit.worker_id, date: tsEdit.date,
        in_time: tsEdit.in_time, out_time: tsEdit.out_time || "",
      });
      toast.success(tsEdit.isNew ? "Giorno aggiunto" : "Orari aggiornati");
      const m = tsEdit.date.slice(0, 7);
      setTsEdit(null);
      if (m !== tsMonth) setTsMonth(m); else loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio orari");
    }
  }

  async function deleteTsDay() {
    const d = new Date(`${tsEdit.date}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
    if (!window.confirm(`Eliminare TUTTE le timbrature di ${tsEdit.worker_name} di ${d}? L'operazione non è reversibile.`)) return;
    try {
      await api.delete("/installers/admin/timbrature/day", { params: { worker_id: tsEdit.worker_id, date: tsEdit.date } });
      toast.success(`Timbrature di ${d} eliminate`);
      setTsEdit(null);
      loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione timbrature");
    }
  }

  async function saveEntryModal() {
    const em = entryModal;
    if (!em.worker_id) { toast.error("Seleziona l'operaio"); return; }
    if (!em.date) { toast.error("Scegli il giorno"); return; }
    if (!em.time) { toast.error("Inserisci l'orario"); return; }
    try {
      if (em.isNew) {
        await api.post("/installers/admin/timbrature", {
          worker_id: em.worker_id, date: em.date, type: em.type, time: em.time, note: em.note || "",
        });
        toast.success("Timbratura aggiunta");
      } else {
        await api.patch(`/installers/admin/timbrature/${em.id}`, {
          date: em.date, type: em.type, time: em.time, note: em.note || "",
        });
        toast.success("Timbratura aggiornata");
      }
      const m = em.date.slice(0, 7);
      setEntryModal(null);
      if (m !== tsMonth) setTsMonth(m); else loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio timbratura");
    }
  }

  async function deleteTimbratura(id) {
    if (!window.confirm("Eliminare questa timbratura? Le ore del giorno verranno ricalcolate.")) return;
    try {
      await api.delete(`/installers/admin/timbrature/${id}`);
      toast.success("Timbratura eliminata");
      loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione timbratura");
    }
  }

  async function runAutoClose() {
    try {
      const { data } = await api.post("/installers/admin/timbrature/auto-close");
      toast.success(data.count > 0 ? `${data.count} giornate chiuse con uscita automatica alle 17:00` : "Tutto ok: nessuna uscita mancante");
      loadTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore verifica uscite");
    }
  }

  const pendingSupplies = (supplies || []).filter((s) => s.status === "pending").length;

  async function openLinkedTask(id) {
    try {
      const { data } = await api.get(`/installers/tasks/${id}`);
      setDupFrom(null);
      if (data.task_type === "activity") setModalActivity(data);
      else setModalTask(data);
    } catch {
      toast.error("Impossibile aprire l'elemento collegato");
    }
  }

  const SUBS = [
    { k: "dashboard", l: "Dashboard", Icon: BarChart3 },
    { k: "consegne", l: "Consegne", Icon: CalendarDays },
    { k: "assistenze", l: "Assistenze", Icon: LifeBuoy },
    { k: "contestazioni", l: "Contestazioni", Icon: AlertTriangle, badge: stats?.overdue_disputes || 0 },
    { k: "attivita", l: "Attività", Icon: ClipboardList },
    { k: "ferramenta", l: "Ferramenta", Icon: Wrench, badge: pendingSupplies },
    { k: "operai", l: "Operai", Icon: HardHat },
    { k: "timbrature", l: "Timbrature", Icon: Clock, badge: pendingAbsCount },
    { k: "statistiche", l: "Statistiche", Icon: BarChart3 },
    { k: "rubrica", l: "Rubrica", Icon: BookUser },
  ];

  return (
    <div data-testid="admin-installers">
      <div className="flex flex-wrap gap-2 mb-6">
        {SUBS.map(({ k, l, Icon, badge }) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase rounded-sm transition-colors ${
              sub === k ? "bg-ink text-cream" : "bg-white text-slate1 border border-ink/10 hover:text-ink"
            }`}
            data-testid={`installers-sub-${k}`}
          >
            <Icon size={13} /> {l}
            {badge > 0 && (
              <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={k === "ferramenta" ? "installers-supplies-badge" : `installers-${k}-badge`}>{badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ---------------- DASHBOARD ---------------- */}
      {sub === "dashboard" && stats && (
        <div className="space-y-6" data-testid="installers-dashboard">
          <InstallerRecentPhotosBar />
          {stats.overdue_disputes > 0 && (
            <button
              onClick={() => setSub("contestazioni")}
              className="w-full bg-rose-600 text-white rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap text-left hover:bg-rose-700 transition-colors"
              data-testid="installers-overdue-banner"
            >
              <AlertTriangle size={18} className="shrink-0" />
              <span className="text-sm font-semibold">
                {stats.overdue_disputes} contestazion{stats.overdue_disputes === 1 ? "e" : "i"} aperte da oltre {stats.overdue_threshold_days || 14} giorni
              </span>
              <span className="ml-auto text-[11px] tracking-[0.14em] uppercase text-white/90 inline-flex items-center gap-1">Vai alle contestazioni <ChevronRight size={14} /></span>
            </button>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              ["Operai totali", stats.total_workers, Users],
              ["Consegne oggi", stats.today_tasks, CalendarDays],
              ["Completate oggi", stats.completed_today, CheckCircle2],
            ].map(([label, val, Icon], i) => (
              <div key={i} className="bg-white border border-ink/10 rounded-sm p-4" data-testid={`installers-stat-${i}`}>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-slate1"><Icon size={12} /> {label}</div>
                <div className="font-display text-3xl text-ink mt-2">{val}</div>
              </div>
            ))}
            {(() => {
              const b = stats.pending_breakdown || { delivery: 0, assistance: 0, activity: 0 };
              const tot = stats.pending_tasks || 0;
              const pct = (n) => (tot > 0 ? Math.round((n / tot) * 100) : 0);
              const rows = [
                ["Consegne", b.delivery, "bg-slate-400"],
                ["Assistenze", b.assistance, "bg-violet-500"],
                ["Attività", b.activity, "bg-amber-500"],
              ];
              return (
                <div className="bg-white border border-ink/10 rounded-sm p-4 col-span-2 lg:col-span-1" data-testid="installers-stat-3">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-slate1"><Package size={12} /> Da fare (tot)</div>
                  <div className="font-display text-3xl text-ink mt-2 mb-2" data-testid="installers-dafare-total">{tot}</div>
                  <div className="space-y-1.5">
                    {rows.map(([lbl, n, color]) => (
                      <div key={lbl} className="flex items-center gap-2 text-[11px]" data-testid={`installers-dafare-${lbl.toLowerCase()}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${color}`} />
                        <span className="text-slate1 flex-1">{lbl}</span>
                        <span className="font-bold text-ink tabular-nums">{n || 0}</span>
                        <span className="text-slate-400 tabular-nums w-9 text-right">{pct(n || 0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {[
              ["Operai al lavoro", stats.active_workers, HardHat],
            ].map(([label, val, Icon], i) => (
              <div key={`b${i}`} className="bg-white border border-ink/10 rounded-sm p-4" data-testid={`installers-stat-4`}>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-slate1"><Icon size={12} /> {label}</div>
                <div className="font-display text-3xl text-ink mt-2">{val}</div>
              </div>
            ))}
          </div>
          {gcal && gcal.accounts.length > 0 && (
            <div className="bg-white border border-ink/10 rounded-sm p-4 flex items-center gap-3 flex-wrap" data-testid="installers-gcal-card">
              <CalendarDays size={16} className="text-slate1" />
              <div>
                <div className="text-sm font-medium text-ink">Consegne su Google Calendar</div>
                <div className="text-[11px] text-slate1">Ogni consegna crea un evento <span className="text-rose-600 font-semibold">rosso</span> con cliente e operai coinvolti</div>
              </div>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <select
                  value={gcal.google_email || ""}
                  onChange={(e) => saveGcal({ google_email: e.target.value })}
                  className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm"
                  data-testid="installers-gcal-account"
                >
                  {gcal.accounts.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/installers/admin/gcal-sync-all");
                      toast.success(data.attempted > 0 ? `${data.attempted} eventi aggiunti al calendario` : "Tutto è già sul calendario");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Errore sincronizzazione");
                    }
                  }}
                  className="px-3 py-2 text-[10px] tracking-wider uppercase font-bold rounded-sm border bg-white text-slate-600 border-ink/15 hover:border-ink/40"
                  data-testid="installers-gcal-syncall"
                >
                  Sincronizza esistenti
                </button>
                <button
                  onClick={() => saveGcal({ enabled: !gcal.enabled })}
                  className={`px-3 py-2 text-[10px] tracking-wider uppercase font-bold rounded-sm border ${gcal.enabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-ink/15"}`}
                  data-testid="installers-gcal-toggle"
                >
                  {gcal.enabled ? "Attiva" : "Disattivata"}
                </button>
              </div>
            </div>
          )}
          {report && (
            <div className="bg-white border border-ink/10 rounded-sm overflow-hidden">
              <div className="p-4 border-b border-ink/10 text-[11px] tracking-[0.2em] uppercase text-slate1">
                Report {report.from} → {report.to} · {report.tasks.total} consegne ({report.tasks.completed} completate)
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-slate1 border-b border-ink/10">
                    <th className="px-4 py-3">Operaio</th>
                    <th className="px-4 py-3 text-right">Assegnate</th>
                    <th className="px-4 py-3 text-right">Completate</th>
                    <th className="px-4 py-3 text-right">Ore lavorate</th>
                  </tr>
                </thead>
                <tbody>
                  {report.per_worker.map((w) => (
                    <tr key={w.worker_id} className="border-b border-ink/5" data-testid={`installers-report-${w.worker_id}`}>
                      <td className="px-4 py-2.5 font-medium text-ink">{w.worker_name || "—"}</td>
                      <td className="px-4 py-2.5 text-right">{w.assigned}</td>
                      <td className="px-4 py-2.5 text-right">{w.completed}</td>
                      <td className="px-4 py-2.5 text-right">{fmtMinutes(w.total_minutes)}</td>
                    </tr>
                  ))}
                  {report.per_worker.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">Nessun dato nel periodo</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ---------------- CONSEGNE (vista settimanale + ricerca) ---------------- */}
      {sub === "consegne" && (() => {
        const searching = q.trim().length > 0;
        const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
        const today = isoDay(new Date());
        const byDay = {};
        for (const t of tasks) (byDay[t.scheduled_date] = byDay[t.scheduled_date] || []).push(t);
        // Vista settimanale: un lavoro su più giorni compare in ogni giornata del suo periodo
        const weekByDay = {};
        for (const d of days) weekByDay[isoDay(d)] = [];
        for (const t of tasks) {
          const { start, end } = taskSpan(t);
          if (!start) continue;
          for (const d of days) {
            const key = isoDay(d);
            if (key >= start && key <= end) weekByDay[key].push(t);
          }
        }
        const absByDay = {};
        for (const a of (assenze || [])) { if (a.status === "approved") (absByDay[a.date] = absByDay[a.date] || []).push(a); }
        const weekLbl = `${weekStart.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${addDays(weekStart, 6).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}`;
        const renderTask = (t) => {
          const loaded = (t.goods || []).filter((g) => g.loaded).length;
          const notFound = (t.goods || []).filter((g) => g.not_found).length;
          const workerNames = (t.workers || []).map((w) => w.name).join(", ");
          return (
            <div key={t.id} className={`bg-white border border-ink/10 rounded-sm p-4 flex items-start gap-4 flex-wrap ${t.task_type === "assistance" ? "border-l-4 border-l-violet-600" : t.task_type === "activity" ? "border-l-4 border-l-amber-500" : ""}`} data-testid={`installers-task-${t.id}`}>
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-ink">{t.scheduled_time && <span className="text-slate1 mr-1">{t.scheduled_time}</span>}{t.title}</span>
                  {t.task_type === "assistance" && (
                    <span className="text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-violet-50 text-violet-900 border-violet-300" data-testid={`installers-task-assist-${t.id}`}>Assistenza</span>
                  )}
                  {t.task_type === "activity" && (
                    <span className="text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-amber-50 text-amber-900 border-amber-300" data-testid={`installers-task-activity-${t.id}`}>Attività</span>
                  )}
                  <span className={`text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${STATUS_STYLE[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                  {t.signed && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`installers-task-signed-${t.id}`}>
                      <PenLine size={10} /> Firmata
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate1 mt-1 flex items-center gap-3 flex-wrap">
                  <span>{[t.client_name && t.client_name !== t.title ? t.client_name : null, t.client_address].filter(Boolean).join(" · ") || null}</span>
                  {t.client_phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {t.client_phone}</span>}
                  {t.riferimento && <span className="inline-flex items-center gap-1 font-medium text-ink" data-testid={`installers-task-rif-${t.id}`}><Tag size={10} /> Rif. {t.riferimento}</span>}
                </div>
                {t.client_delivery_notes && (
                  <div className="text-[11px] text-amber-900 mt-1 inline-flex items-start gap-1.5 bg-amber-50 border border-amber-100 rounded-sm px-2 py-1" data-testid={`installers-task-dnotes-${t.id}`}>
                    <StickyNote size={11} className="shrink-0 mt-0.5 text-amber-600" /> {t.client_delivery_notes}
                  </div>
                )}
                {t.linked_task && (
                  <div className="mt-1">
                    <button onClick={() => openLinkedTask(t.linked_task.id)} className="text-[11px] text-sky-900 inline-flex items-center gap-1.5 bg-sky-50 border border-sky-200 rounded-sm px-2 py-1 hover:bg-sky-100" data-testid={`installers-task-linked-${t.id}`} title="Apri la consegna/assistenza collegata">
                    <Link2 size={11} className="shrink-0 text-sky-600" /> {t.linked_task.task_type === "assistance" ? "Assistenza" : "Consegna"} collegata: <b>{t.linked_task.client_name || t.linked_task.title}</b>{t.linked_task.scheduled_date ? ` · ${t.linked_task.scheduled_date}` : ""}
                    </button>
                  </div>
                )}
                <div className="text-xs text-slate1 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1" data-testid={`installers-task-workers-${t.id}`}><HardHat size={11} /> {workerNames || "Non assegnata"}</span>
                  {taskSpan(t).total > 1 && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5" data-testid={`installers-task-span-${t.id}`}>
                      <CalendarRange size={11} /> {taskSpan(t).total} giorni
                    </span>
                  )}
                  {(t.goods || []).length > 0 && <span className="inline-flex items-center gap-1"><Package size={11} /> Merce {loaded}/{(t.goods || []).length}</span>}
                  {notFound > 0 && (
                    <span className="text-[9px] tracking-wider uppercase font-bold text-rose-700 bg-rose-50 border border-rose-300 rounded px-1.5 py-0.5" data-testid={`installers-task-notfound-${t.id}`}>
                      {notFound} non trovat{notFound === 1 ? "o" : "i"}
                    </span>
                  )}
                  <span>📷 {t.photo_count} · 📝 {t.note_count} · 📎 {t.attachment_count}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {t.signed && (
                  <a
                    href={`${API}${t.proof_pdf_url.replace("/api", "")}?token=${localStorage.getItem("ah_token") || ""}`}
                    target="_blank" rel="noreferrer"
                    className="p-2 text-emerald-700 hover:text-emerald-800 rounded-sm hover:bg-emerald-50"
                    title="Scarica prova di consegna (PDF)"
                    data-testid={`installers-task-proof-${t.id}`}
                  >
                    <FileDown size={15} />
                  </a>
                )}
                <select
                  value={t.status}
                  onChange={(e) => changeStatus(t, e.target.value)}
                  className="border border-ink/15 px-2 py-1.5 rounded-sm bg-cream text-xs"
                  data-testid={`installers-task-status-${t.id}`}
                >
                  <option value="pending">Da fare</option>
                  <option value="in_progress">In corso</option>
                  <option value="completed">Completata</option>
                </select>
                {t.task_type !== "activity" && <button onClick={() => { setDupFrom(t); setModalTask(null); }} className="p-2 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Duplica consegna" data-testid={`installers-task-dup-${t.id}`}><Copy size={15} /></button>}
                <button onClick={() => { setDupFrom(null); if (t.task_type === "activity") setModalActivity(t); else setModalTask(t); }} className="p-2 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Modifica" data-testid={`installers-task-edit-${t.id}`}><Pencil size={15} /></button>
                <button onClick={() => deleteTask(t)} className="p-2 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" title="Elimina" data-testid={`installers-task-del-${t.id}`}><Trash2 size={15} /></button>
              </div>
              {viewMode === "incorso" && !searching && (
                <div className="w-full flex items-center gap-2 pt-2.5 mt-1 border-t border-ink/10 flex-wrap">
                  <CalendarDays size={13} className="text-slate1" />
                  <span className="text-[10px] tracking-[0.15em] uppercase text-slate1 font-bold">Riprogramma al</span>
                  <input
                    type="date"
                    value={reschedDates[t.id] ?? t.scheduled_date}
                    onChange={(e) => setReschedDates((p) => ({ ...p, [t.id]: e.target.value }))}
                    className="border border-ink/20 px-2.5 py-1.5 rounded-sm bg-cream text-xs"
                    data-testid={`installers-resched-date-${t.id}`}
                  />
                  <button
                    onClick={() => rescheduleTask(t)}
                    disabled={!reschedDates[t.id] || reschedDates[t.id] === t.scheduled_date}
                    className="inline-flex items-center gap-1.5 bg-ink text-cream px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase rounded-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`installers-resched-save-${t.id}`}
                  >
                    <RefreshCw size={11} /> Riprogramma
                  </button>
                  <span className="text-[10px] text-slate1">Attualmente: {new Date(`${t.scheduled_date}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}</span>
                </div>
              )}
            </div>
          );
        };
        return (
          <div className="space-y-4" data-testid="installers-consegne">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center bg-white border border-ink/10 rounded-sm overflow-hidden">
                <button onClick={() => setViewMode("week")} className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-[11px] tracking-wider uppercase ${viewMode === "week" ? "bg-ink text-cream font-bold" : "text-slate1 hover:text-ink"}`} data-testid="installers-view-week"><CalendarDays size={13} /> Settimana</button>
                <button onClick={() => setViewMode("list")} className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-[11px] tracking-wider uppercase ${viewMode === "list" ? "bg-ink text-cream font-bold" : "text-slate1 hover:text-ink"}`} data-testid="installers-view-list"><List size={13} /> Lista</button>
                <button onClick={() => setViewMode("incorso")} className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-[11px] tracking-wider uppercase ${viewMode === "incorso" ? "bg-ink text-cream font-bold" : "text-slate1 hover:text-ink"}`} data-testid="installers-view-incorso"><Clock size={13} /> In corso</button>
              </div>
              {!searching && viewMode === "week" && (
                <>
                  <div className="flex items-center bg-white border border-ink/10 rounded-sm">
                    <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-2.5 text-slate1 hover:text-ink" title="Settimana precedente" data-testid="installers-week-prev"><ChevronLeft size={16} /></button>
                    <span className="px-2 text-sm font-medium text-ink min-w-[185px] text-center capitalize" data-testid="installers-week-label">{weekLbl}</span>
                    <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-2.5 text-slate1 hover:text-ink" title="Settimana successiva" data-testid="installers-week-next"><ChevronRight size={16} /></button>
                  </div>
                  <button onClick={() => setWeekStart(mondayOf(new Date()))} className="px-3 py-2.5 bg-white border border-ink/10 rounded-sm text-[11px] tracking-wider uppercase text-slate1 hover:text-ink" data-testid="installers-week-today">Oggi</button>
                </>
              )}
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Cerca consegne (cliente, indirizzo, telefono, merce…)"
                  className="w-full border border-ink/20 pl-9 pr-9 py-2.5 rounded-sm bg-white text-sm"
                  data-testid="installers-search"
                />
                {qInput && (
                  <button onClick={() => setQInput("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-ink" data-testid="installers-search-clear"><X size={14} /></button>
                )}
              </div>
              {viewMode !== "incorso" && (
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm" data-testid="installers-status-filter">
                  <option value="">Tutti (incl. completate)</option>
                  <option value="active">Da fare + in corso</option>
                  <option value="pending">Da fare</option>
                  <option value="in_progress">In corso</option>
                  <option value="completed">Completate</option>
                </select>
              )}
              <div className="ml-auto relative">
                <button
                  onClick={() => setTypeMenu((m) => (m === "main" ? null : "main"))}
                  className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm"
                  data-testid="installers-new-task-btn"
                >
                  <Plus size={14} /> Nuova
                </button>
                {typeMenu === "main" && (
                  <>
                    <div className="fixed inset-0 z-[40]" onClick={() => setTypeMenu(null)} data-testid="installers-new-type-overlay" />
                    <div className="absolute right-0 mt-1 z-[50] bg-white border border-ink/15 rounded-sm shadow-lg py-1 min-w-[190px]" data-testid="installers-new-type-menu">
                      {NEW_TYPES.map(({ type, label, Icon }) => (
                        <button key={type} onClick={() => chooseNewType(type, null)} className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[11px] tracking-[0.14em] uppercase text-ink hover:bg-cream2 transition-colors" data-testid={`installers-new-type-${type}`}>
                          <Icon size={14} /> {label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {searching ? (
              tasks.length === 0 ? (
                <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="installers-no-search-results">
                  Nessuna consegna trovata per «{q.trim()}»
                </div>
              ) : (
                <div className="space-y-4" data-testid="installers-search-results">
                  <div className="text-xs text-slate1">{tasks.length} consegn{tasks.length === 1 ? "a trovata" : "e trovate"} per «{q.trim()}»</div>
                  {Object.keys(byDay).sort().map((key) => (
                    <div key={key}>
                      <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-slate1 mb-1.5 capitalize">
                        {new Date(`${key}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      </div>
                      <div className="space-y-2">{byDay[key].map(renderTask)}</div>
                    </div>
                  ))}
                </div>
              )
            ) : viewMode === "list" ? (
              <div className="space-y-4" data-testid="installers-list-view">
                {tasks.length === 0 ? (
                  <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="installers-list-empty">Nessuna consegna in programma</div>
                ) : (() => {
                  const dayKeys = Object.keys(byDay).sort();
                  const anchorKey = dayKeys.find((k) => k >= today) || dayKeys[dayKeys.length - 1];
                  return dayKeys.map((key) => {
                    const overdue = key < today;
                    const isAnchor = key === anchorKey;
                    return (
                      <div
                        key={key}
                        style={isAnchor ? { scrollMarginTop: 130 } : undefined}
                        className={key < anchorKey ? "opacity-55" : ""}
                        data-testid={isAnchor ? "installers-list-anchor" : undefined}
                      >
                        <div className={`text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5 capitalize flex items-center gap-2 ${overdue ? "text-rose-600" : key === today ? "text-ink" : "text-slate1"}`}>
                          {new Date(`${key}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                          {overdue && <span className="normal-case">· in ritardo</span>}
                          {isAnchor && anchorKey >= today && !q.trim() && <span className="bg-ink text-cream rounded-sm px-2 py-0.5 text-[9px] tracking-[0.2em]" data-testid="installers-next-badge">▸ Prossima consegna</span>}
                        </div>
                        <div className="space-y-2">{byDay[key].map(renderTask)}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : viewMode === "incorso" ? (
              <div className="space-y-4" data-testid="installers-incorso-view">
                <div className="text-xs text-slate1">{tasks.length === 0 ? "" : `${tasks.length} consegn${tasks.length === 1 ? "a" : "e"} in corso — cambia la data per riprogrammarle.`}</div>
                {tasks.length === 0 ? (
                  <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="installers-incorso-empty">Nessuna consegna in corso</div>
                ) : (
                  Object.keys(byDay).sort().map((key) => {
                    const overdue = key < today;
                    return (
                      <div key={key}>
                        <div className={`text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5 capitalize ${overdue ? "text-rose-600" : key === today ? "text-ink" : "text-slate1"}`}>
                          {new Date(`${key}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                          {overdue && <span className="ml-2 normal-case">· in ritardo</span>}
                        </div>
                        <div className="space-y-2">{byDay[key].map(renderTask)}</div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="installers-week-grid">
                {days.map((d) => {
                  const key = isoDay(d);
                  const dayTasks = weekByDay[key] || [];
                  const isToday = key === today;
                  const isPast = key < today;
                  const cellClass = isToday
                    ? "bg-[#E8DDC7] border-gold ring-1 ring-gold/40 shadow-inner"
                    : isPast
                      ? "bg-cream2/50 border-ink/5"
                      : "bg-white border-ink/10";
                  return (
                    <div
                      key={key}
                      className={`group relative border rounded-sm p-3 min-h-[150px] ${cellClass} transition-colors ${dragOverDay === key ? "ring-2 ring-gold bg-amber-50/60" : ""} ${typeMenu === key ? "z-30" : ""}`}
                      data-testid={`installers-day-${key}`}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverDay !== key) setDragOverDay(key); }}
                      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverDay(null); }}
                      onDrop={(e) => { e.preventDefault(); setDragOverDay(null); const raw = e.dataTransfer.getData("text/plain"); if (!raw) return; const [id, srcDay] = raw.split("|"); if (id) moveTaskToDate(id, key, srcDay || null); }}
                    >
                      <div className="flex items-baseline justify-between mb-2 pb-1 border-b border-ink/10">
                        <div className="text-xs font-display uppercase text-ink">
                          <span className="tracking-wide">{d.toLocaleDateString("it-IT", { weekday: "short" })}</span>
                          <span className="ml-2 font-mono opacity-80">{d.getDate()}</span>
                          {isToday && <span className="ml-2 text-[9px] tracking-[0.2em] text-gold font-bold">· OGGI</span>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[9px] tracking-[0.15em] uppercase ${isToday ? "text-ink/70" : "text-slate1"}`}>
                            {dayTasks.length === 0 ? "—" : `${dayTasks.length} cons.`}
                          </span>
                          <span className="relative inline-flex">
                            <button
                              type="button"
                              onClick={() => setTypeMenu((m) => (m === key ? null : key))}
                              className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink/5 hover:bg-gold hover:text-white text-ink transition-opacity ${typeMenu === key ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                              data-testid={`installers-day-add-${key}`}
                              title={`Nuovo elemento · ${d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "short" })}`}
                            >
                              <Plus size={11} strokeWidth={2.5} />
                            </button>
                            {typeMenu === key && (
                              <>
                                <div className="fixed inset-0 z-[40]" onClick={(e) => { e.stopPropagation(); setTypeMenu(null); }} />
                                <div className="absolute right-0 top-6 z-[50] bg-white border border-ink/15 rounded-sm shadow-lg py-1 min-w-[168px]" data-testid={`installers-day-add-menu-${key}`}>
                                  {NEW_TYPES.map(({ type, label, Icon }) => (
                                    <button key={type} onClick={(e) => { e.stopPropagation(); chooseNewType(type, key); }} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.12em] uppercase text-ink hover:bg-cream2 transition-colors" data-testid={`installers-day-add-${type}-${key}`}>
                                      <Icon size={13} /> {label}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {dayTasks.map((t) => {
                          const loaded = (t.goods || []).filter((g) => g.loaded).length;
                          const workerNames = (t.workers || []).map((w) => (w.name || "").split(" ")[0]).join(", ");
                          const span = dayInSpan(t, key);
                          return (
                            <div key={t.id} className="relative group/task">
                            <button
                              type="button"
                              draggable
                              onDragStart={(e) => { e.dataTransfer.setData("text/plain", `${t.id}|${key}`); e.dataTransfer.effectAllowed = "move"; }}
                              onDragEnd={() => setDragOverDay(null)}
                              onClick={() => { setDupFrom(null); if (t.task_type === "activity") setModalActivity(t); else setModalTask(t); }}
                              className={`w-full text-left text-[11px] py-1 px-1.5 rounded-sm border transition-colors hover:opacity-75 cursor-grab active:cursor-grabbing ${STATUS_CHIP[t.status] || STATUS_CHIP.pending} ${t.task_type === "assistance" ? "border-l-4 border-l-violet-600" : t.task_type === "activity" ? "border-l-4 border-l-amber-500" : ""}`}
                              data-testid={`installers-week-task-${t.id}`}
                              title={`${t.title} · ${t.task_type === "assistance" ? "Assistenza · " : t.task_type === "activity" ? "Attività · " : ""}${STATUS_LABEL[t.status]}${span ? ` · lavoro su ${span.total} giorni (giorno ${span.idx})` : ""} — clicca per aprire, trascina su un altro giorno per riprogrammare`}
                            >
                              <div className="flex items-baseline gap-1.5">
                                <span className="font-mono text-[10px] opacity-80">{t.scheduled_time || "—"}</span>
                                <span className={`flex-1 min-w-0 truncate font-medium ${t.status === "completed" ? "line-through opacity-70" : ""}`}>{t.title}</span>
                                {t.signed && <PenLine size={9} className="shrink-0 text-emerald-700" />}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[9px] opacity-75">
                                {span && <span className="inline-flex items-center gap-0.5 uppercase tracking-wider font-bold text-sky-800 bg-sky-100 rounded px-1" data-testid={`installers-week-span-${t.id}`}><CalendarRange size={8} /> G{span.idx}/{span.total}</span>}
                                {t.task_type === "assistance" && <span className="uppercase tracking-wider font-bold text-violet-800" data-testid={`installers-week-assist-${t.id}`}>Assist.</span>}
                                {t.task_type === "activity" && <span className="uppercase tracking-wider font-bold text-amber-700" data-testid={`installers-week-activity-${t.id}`}>Attiv.</span>}
                                <span className="uppercase tracking-wider font-bold" data-testid={`installers-week-status-${t.id}`}>{t.status === "completed" ? "Fatta" : STATUS_LABEL[t.status]}</span>
                                <span className="inline-flex items-center gap-0.5"><HardHat size={8} /> {workerNames || "Non assegnata"}</span>
                                {(t.goods || []).length > 0 && <span className="inline-flex items-center gap-0.5"><Package size={8} /> {loaded}/{(t.goods || []).length}</span>}
                                {t.riferimento && <span className="inline-flex items-center gap-0.5"><Tag size={8} /> {t.riferimento}</span>}
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteTask(t); }}
                              className="absolute top-0.5 right-0.5 p-0.5 rounded-sm bg-white/80 text-rose-600 hover:bg-rose-600 hover:text-white opacity-100 md:opacity-0 md:group-hover/task:opacity-100 transition-opacity"
                              title="Elimina"
                              data-testid={`installers-week-del-${t.id}`}
                            >
                              <Trash2 size={11} />
                            </button>
                            </div>
                          );
                        })}
                        {(absByDay[key] || []).map((a) => (
                          <div
                            key={a.id}
                            className="w-full text-left text-[11px] py-1 px-1.5 rounded-sm border border-sky-200 border-l-4 border-l-sky-500 bg-sky-50 text-sky-900"
                            data-testid={`installers-week-absence-${a.id}`}
                            title={`Assenza · ${a.worker_name} — ${ABS_LABEL[a.type] || a.type}`}
                          >
                            <div className="flex items-baseline gap-1.5">
                              <HardHat size={9} className="shrink-0 text-sky-700" />
                              <span className="flex-1 min-w-0 truncate font-medium">{a.worker_name}</span>
                              <span className="text-[9px] uppercase tracking-wider font-bold shrink-0">{ABS_LABEL[a.type] || a.type}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ---------------- FERRAMENTA ---------------- */}
      {sub === "ferramenta" && (
        <div className="space-y-4" data-testid="installers-ferramenta">
          <form onSubmit={addSupply} className="bg-white border border-ink/10 rounded-sm p-4 flex flex-wrap items-center gap-2">
            <input required placeholder="Articolo (es. Viti 4x40)" value={sForm.item} onChange={(e) => setSForm({ ...sForm, item: e.target.value })} className="flex-1 min-w-[180px] border border-ink/15 px-3 py-2.5 rounded-sm bg-cream text-sm" data-testid="installers-supply-item" />
            <input placeholder="Quantità (es. 2 scatole)" value={sForm.qty} onChange={(e) => setSForm({ ...sForm, qty: e.target.value })} className="w-44 border border-ink/15 px-3 py-2.5 rounded-sm bg-cream text-sm" data-testid="installers-supply-qty" />
            <input placeholder="Nota" value={sForm.note} onChange={(e) => setSForm({ ...sForm, note: e.target.value })} className="flex-1 min-w-[140px] border border-ink/15 px-3 py-2.5 rounded-sm bg-cream text-sm" data-testid="installers-supply-note" />
            <button className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="installers-supply-add">
              <Plus size={13} /> Aggiungi
            </button>
          </form>

          <div className="flex items-center gap-2">
            {[["", "Tutte"], ["pending", "Da ordinare"], ["purchased", "Acquistate"]].map(([val, label]) => (
              <button key={val} onClick={() => setSupplyFilter(val)} className={`px-3 py-2 text-[11px] tracking-wider uppercase rounded-sm ${supplyFilter === val ? "bg-ink text-cream" : "bg-white border border-ink/10 text-slate1"}`} data-testid={`installers-supply-filter-${val || "all"}`}>
                {label}
              </button>
            ))}
            <button onClick={loadSupplies} className="p-2.5 bg-white border border-ink/10 rounded-sm text-slate1 hover:text-ink ml-auto" data-testid="installers-supplies-refresh"><RefreshCw size={15} /></button>
          </div>

          {!supplies ? (
            <div className="flex justify-center py-12 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
          ) : supplies.length === 0 ? (
            <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="installers-no-supplies">
              Nessuna richiesta di ferramenta{supplyFilter ? " con questo filtro" : ": gli operai possono segnalarla dall'app, oppure aggiungila qui sopra"}.
            </div>
          ) : (
            <div className="space-y-2">
              {supplies.map((s) => (
                <div key={s.id} className={`bg-white border rounded-sm p-4 flex items-center gap-3 flex-wrap ${s.status === "pending" ? "border-amber-300" : "border-ink/10 opacity-75"}`} data-testid={`installers-supply-${s.id}`}>
                  {s.photo_url && (
                    <a href={`${API}/installers${s.photo_url}?token=${localStorage.getItem("ah_token") || ""}`} target="_blank" rel="noreferrer" title="Apri foto del pezzo" data-testid={`installers-supply-photo-${s.id}`}>
                      <img src={`${API}/installers${s.photo_url}?token=${localStorage.getItem("ah_token") || ""}`} alt={s.item} className="w-14 h-14 object-cover rounded-sm bg-cream2 border border-ink/10 hover:opacity-80" loading="lazy" />
                    </a>
                  )}
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${s.status === "purchased" ? "line-through text-slate-400" : "text-ink"}`}>{s.item}</span>
                      {s.qty && <span className="text-sm text-slate-600">· {s.qty}</span>}
                      <span className={`text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${s.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                        {s.status === "pending" ? "Da ordinare" : "Acquistata"}
                      </span>
                    </div>
                    <div className="text-xs text-slate1 mt-1 flex items-center gap-2 flex-wrap">
                      <span>{s.created_by_name}</span>
                      <span>· {new Date(s.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                      {s.task_title && <span>· Consegna: {s.task_title}</span>}
                      {s.note && <span>· {s.note}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.status === "pending" ? (
                      <button onClick={() => setSupplyStatus(s, "purchased")} className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-[10px] tracking-wider uppercase rounded-sm font-bold" data-testid={`installers-supply-buy-${s.id}`}>
                        <CheckCircle2 size={12} /> Acquistata
                      </button>
                    ) : (
                      <button onClick={() => setSupplyStatus(s, "pending")} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-ink/15 text-slate-600 text-[10px] tracking-wider uppercase rounded-sm" data-testid={`installers-supply-undo-${s.id}`}>
                        <Undo2 size={12} /> Da ordinare
                      </button>
                    )}
                    <button onClick={() => deleteSupply(s)} className="p-2 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" data-testid={`installers-supply-del-${s.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- OPERAI ---------------- */}
      {sub === "operai" && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="installers-operai-subtabs">
          {[["operai", "Operai", HardHat], ["bustepaga", "Buste paga", Receipt], ["sedi", "Sedi / Cantieri", MapPin]].map(([k, l, IconX]) => (
            <button key={k} onClick={() => setOperaiTab(k)} className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.16em] uppercase rounded-sm transition-colors ${operaiTab === k ? "bg-ink text-cream" : "bg-white text-slate1 border border-ink/10 hover:text-ink"}`} data-testid={`operai-subtab-${k}`}>
              <IconX size={13} /> {l}
            </button>
          ))}
        </div>
      )}
      {sub === "operai" && operaiTab === "operai" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="installers-operai">
          <form onSubmit={createWorker} className="bg-white border border-ink/10 p-6 rounded-sm space-y-3" data-testid="installers-worker-form">
            <div className="font-display text-lg text-ink">Nuovo operaio</div>
            <input required placeholder="Nome e cognome" value={wForm.name} onChange={(e) => setWForm({ ...wForm, name: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="installers-worker-name" />
            <input required type="email" placeholder="Email (login app)" value={wForm.email} onChange={(e) => setWForm({ ...wForm, email: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="installers-worker-email" />
            <input required type="password" autoComplete="new-password" placeholder="Password (min 6 caratteri)" value={wForm.password} onChange={(e) => setWForm({ ...wForm, password: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="installers-worker-password" />
            <input placeholder="Telefono" value={wForm.phone} onChange={(e) => setWForm({ ...wForm, phone: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="installers-worker-phone" />
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Qualifica</label>
              <select value={wForm.qualifica} onChange={(e) => setWForm({ ...wForm, qualifica: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm" data-testid="installers-worker-qualifica">
                <option value="operaio">Operaio</option>
                <option value="responsabile">Responsabile</option>
              </select>
              {wForm.qualifica === "responsabile" && <p className="mt-1 text-[10px] text-indigo-800">Vede tutte le consegne (con foto e assegnazioni) e gestisce la ferramenta. Non può creare, modificare, assegnare o cancellare consegne.</p>}
            </div>
            <button className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="installers-worker-create">Crea operaio</button>
            <p className="text-[11px] text-slate1">L&apos;operaio userà email e password per accedere all&apos;app mobile Archi-Hub Installers.</p>
          </form>
          <div className="lg:col-span-2 bg-white border border-ink/10 rounded-sm divide-y divide-ink/5">
            {workers.length === 0 && <div className="p-8 text-center text-sm text-slate-400">Nessun operaio</div>}
            {workers.map((w) => (
              <div key={w.id} className={`p-4 flex items-center gap-3 ${w.privacy_consent ? "" : "bg-rose-50/70 border-l-2 border-rose-400"}`} data-testid={`installers-worker-${w.id}`}>
                <div className="w-9 h-9 rounded-full bg-ink text-cream flex items-center justify-center text-sm font-semibold shrink-0">
                  {(w.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-ink font-medium flex items-center gap-2 flex-wrap">
                    {w.name}
                    {w.qualifica === "responsabile" && (
                      <span className="text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-indigo-50 text-indigo-900 border-indigo-300" data-testid={`worker-resp-badge-${w.id}`}>Responsabile</span>
                    )}
                  </div>
                  <div className="text-xs text-slate1">{w.email}{w.phone ? ` · ${w.phone}` : ""}</div>
                  {w.privacy_consent ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`worker-privacy-ok-${w.id}`}>
                      <ShieldCheck size={10} /> Consenso privacy ✓
                      {w.privacy_consent_at ? ` · ${new Date(w.privacy_consent_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
                      {w.privacy_version ? ` · v${w.privacy_version}` : ""}
                    </div>
                  ) : (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-rose-700 bg-rose-100 border border-rose-300 rounded px-1.5 py-0.5" data-testid={`worker-privacy-missing-${w.id}`}>
                      <ShieldAlert size={10} /> Consenso privacy non registrato
                    </div>
                  )}
                </div>
                <button onClick={() => setWEdit({ id: w.id, name: w.name || "", email: w.email || "", phone: w.phone || "", qualifica: w.qualifica || "operaio", password: "", password_admin: w.password_admin || "", showPwd: false })} className="p-2 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Modifica operaio" data-testid={`installers-worker-edit-${w.id}`}><Pencil size={15} /></button>
                <button onClick={() => { setWDelete(w); setWDelStep(1); setWDelText(""); }} className="p-2 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" title="Elimina operaio" data-testid={`installers-worker-del-${w.id}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- TIMBRATURE (vista mensile per operaio) ---------------- */}
      {sub === "timbrature" && (
        <div className="space-y-4" data-testid="installers-timbrature">
          {/* Richieste di assenza operai: approva / rifiuta / modifica / elimina + storico */}
          <div className="bg-white border border-ink/10 rounded-sm" data-testid="installers-assenze-panel">
            <div className="flex items-center gap-2 flex-wrap p-4 border-b border-ink/10">
              <div className="font-medium text-ink inline-flex items-center gap-2"><CalendarDays size={15} /> Richieste di assenza operai</div>
              {pendingAbsCount > 0 && (
                <span className="min-w-[20px] h-5 px-2 rounded-full bg-rose-500 text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="installers-assenze-pending-count">{pendingAbsCount} in attesa</span>
              )}
              <div className="ml-auto inline-flex rounded-sm border border-ink/10 overflow-hidden">
                <button onClick={() => setAssFilter("pending")} className={`px-3 py-2 text-[10px] tracking-wider uppercase font-bold transition-colors ${assFilter === "pending" ? "bg-ink text-cream" : "bg-white text-slate1 hover:text-ink"}`} data-testid="installers-assenze-filter-pending">In attesa{pendingAbsCount > 0 ? ` (${pendingAbsCount})` : ""}</button>
                <button onClick={() => setAssFilter("storico")} className={`px-3 py-2 text-[10px] tracking-wider uppercase font-bold transition-colors border-l border-ink/10 ${assFilter === "storico" ? "bg-ink text-cream" : "bg-white text-slate1 hover:text-ink"}`} data-testid="installers-assenze-filter-storico">Storico</button>
              </div>
            </div>
            <div className="p-4">
              {assenze === null ? (
                <div className="flex justify-center py-6 text-slate-400"><Loader2 size={18} className="animate-spin" /></div>
              ) : (() => {
                const list = assFilter === "pending"
                  ? assenze.filter((a) => a.status === "pending")
                  : assenze.filter((a) => a.status !== "pending");
                if (list.length === 0) {
                  return <div className="text-center py-6 text-sm text-slate-400" data-testid="installers-assenze-empty">{assFilter === "pending" ? "Nessuna richiesta di assenza in attesa" : "Nessuna richiesta gestita finora"}</div>;
                }
                return (
                  <div className="space-y-2">
                    {list.map((a) => (
                      <div key={a.id} className="flex items-center gap-3 flex-wrap border border-ink/10 rounded-sm p-3 bg-cream2/40" data-testid={`installers-assenza-${a.id}`}>
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink"><HardHat size={14} className="text-slate1" /> {a.worker_name}</span>
                        <span className={`text-[10px] tracking-wider uppercase font-bold border rounded px-2 py-0.5 ${ABS_STYLE[a.type] || "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`installers-assenza-type-${a.id}`}>{ABS_LABEL[a.type] || a.type}</span>
                        <span className="text-sm text-ink capitalize">{new Date(`${a.date}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                        {a.status === "pending" ? (
                          <span className="text-[11px] text-slate1">Richiesta {a.requested_at ? new Date(a.requested_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                        ) : (
                          <span className={`text-[10px] tracking-wider uppercase font-bold border rounded px-2 py-0.5 ${a.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`} data-testid={`installers-assenza-status-${a.id}`}>{a.status === "approved" ? `Approvata${a.approved_by ? ` · ${a.approved_by}` : ""}` : `Rifiutata${a.rejected_by ? ` · ${a.rejected_by}` : ""}`}</span>
                        )}
                        <div className="ml-auto flex items-center gap-1.5">
                          {a.status !== "approved" && (
                            <button onClick={() => reviewAbsence(a, "approve")} disabled={absBusyId === a.id} className="inline-flex items-center gap-1 px-3 py-1.5 border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 text-[10px] tracking-wider uppercase rounded-sm font-bold disabled:opacity-50 transition-colors" data-testid={`installers-assenza-approve-${a.id}`}>{absBusyId === a.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Approva</button>
                          )}
                          {a.status !== "rejected" && (
                            <button onClick={() => reviewAbsence(a, "reject")} disabled={absBusyId === a.id} className="inline-flex items-center gap-1 px-3 py-1.5 border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 text-[10px] tracking-wider uppercase rounded-sm font-bold disabled:opacity-50 transition-colors" data-testid={`installers-assenza-reject-${a.id}`}><X size={11} /> Rifiuta</button>
                          )}
                          <button onClick={() => setAbsEdit({ id: a.id, worker_name: a.worker_name, type: a.type, date: a.date })} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Modifica tipo/data" data-testid={`installers-assenza-edit-${a.id}`}><Pencil size={13} /></button>
                          <button onClick={() => deleteAbsenceReq(a)} className="p-1.5 text-slate1 hover:text-rose-600 rounded-sm hover:bg-rose-50" title="Elimina richiesta" data-testid={`installers-assenza-del-${a.id}`}><Trash2 size={13} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center bg-white border border-ink/10 rounded-sm">
              <button onClick={() => setTsMonth(shiftMonth(tsMonth, -1))} className="p-2.5 text-slate1 hover:text-ink" title="Mese precedente" data-testid="installers-month-prev"><ChevronLeft size={16} /></button>
              <span className="px-2 text-sm font-medium text-ink min-w-[150px] text-center capitalize" data-testid="installers-month-label">{monthLabel(tsMonth)}</span>
              <button onClick={() => setTsMonth(shiftMonth(tsMonth, 1))} className="p-2.5 text-slate1 hover:text-ink" title="Mese successivo" data-testid="installers-month-next"><ChevronRight size={16} /></button>
            </div>
            <button onClick={() => setTsMonth(new Date().toLocaleDateString("sv-SE").slice(0, 7))} className="px-3 py-2.5 bg-white border border-ink/10 rounded-sm text-[11px] tracking-wider uppercase text-slate1 hover:text-ink" data-testid="installers-month-today">Mese corrente</button>
            <button onClick={runAutoClose} className="inline-flex items-center gap-2 px-3 py-2.5 bg-white border border-ink/10 rounded-sm text-[11px] tracking-wider uppercase text-slate1 hover:text-ink" data-testid="installers-ts-autoclose">
              <ShieldCheck size={13} /> Verifica uscite
            </button>
            <button
              onClick={() => setTsEdit({ worker_id: "", worker_name: "", date: "", in_time: "08:00", out_time: "17:00", isNew: true })}
              className="inline-flex items-center gap-2 px-3 py-2.5 bg-ink text-cream rounded-sm text-[11px] tracking-wider uppercase"
              data-testid="installers-ts-addday"
            >
              <CalendarPlus size={13} /> Aggiungi giorno
            </button>
            <button
              onClick={() => setEntryModal({ id: null, worker_id: tsWorker || "", worker_name: "", date: "", type: "in", time: "08:00", note: "", isNew: true })}
              className="inline-flex items-center gap-2 px-3 py-2.5 bg-white border border-ink/20 text-ink rounded-sm text-[11px] tracking-wider uppercase hover:bg-ink hover:text-cream transition-colors"
              data-testid="installers-ts-addentry"
            >
              <Plus size={13} /> Aggiungi timbratura
            </button>
            <button onClick={loadTimesheets} className="p-2.5 bg-white border border-ink/10 rounded-sm text-slate1 hover:text-ink" data-testid="installers-ts-refresh"><RefreshCw size={15} /></button>
          </div>
          <div className="text-[11px] text-slate1" data-testid="installers-ts-legend">
            Straordinario oltre le <b>9 ore</b> al giorno. Meno di <b>5 ore</b> lavorate = <span className="text-orange-700 font-semibold">mezza giornata</span>; sotto le <b>4 ore</b> la differenza (4h − lavorate) conta come ore mancanti. I giorni interi sotto le 9h mostrano le <span className="text-rose-600 font-semibold">ore mancanti (−)</span>: a fine mese vengono compensate con gli straordinari e l&apos;eccedenza non coperta è conteggiata come <span className="text-rose-600 font-semibold">permesso</span>. Alle <b>17:15</b> chi ha il turno ancora aperto riceve un promemoria per timbrare l&apos;uscita. Controllo automatico ogni sera alle <b>19:30</b> (e di nuovo alle 3:00 di notte come sicurezza) — se manca l&apos;uscita, viene inserita l&apos;uscita alle 17:00 e il giorno appare <span className="text-rose-600 font-semibold">in rosso</span>. Ogni timbratura mostra la posizione GPS (clicca per aprire la mappa): <span className="text-rose-600 font-semibold">rossa</span> se a più di 2 km dalla sede del QR. Un giorno feriale senza ingresso timbrato entro le <b>14:00</b> diventa automaticamente <span className="text-violet-700 font-semibold">Permesso giornata intera</span> (9h, non compensato con gli straordinari) — sabato, domenica e festivi esclusi.
          </div>
          {!timesheets ? (
            <div className="flex justify-center py-12 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
          ) : timesheets.workers.length === 0 ? (
            <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="installers-no-ts">Nessuna timbratura a {monthLabel(tsMonth)}</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2" data-testid="installers-ts-worker-tabs">
                <button
                  onClick={() => setTsWorker("")}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] tracking-wider uppercase rounded-sm border transition-colors ${tsWorker === "" ? "bg-ink text-cream border-ink font-bold" : "bg-white text-slate1 border-ink/10 hover:text-ink"}`}
                  data-testid="installers-ts-worker-all"
                >
                  <Users size={12} /> Tutti
                </button>
                {timesheets.workers.map((w) => (
                  <button
                    key={w.worker_id}
                    onClick={() => setTsWorker(w.worker_id)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] tracking-wider uppercase rounded-sm border transition-colors ${tsWorker === w.worker_id ? "bg-ink text-cream border-ink font-bold" : "bg-white text-slate1 border-ink/10 hover:text-ink"}`}
                    data-testid={`installers-ts-worker-tab-${w.worker_id}`}
                  >
                    <HardHat size={12} /> {w.worker_name}
                  </button>
                ))}
              </div>
              <div className="space-y-5">
                {timesheets.workers.filter((w) => !tsWorker || w.worker_id === tsWorker).map((w) => (
                <div key={w.worker_id} className="bg-white border border-ink/10 rounded-sm overflow-hidden" data-testid={`installers-ts-${w.worker_id}`}>
                  <div className="p-4 border-b border-ink/10 flex items-center gap-3 flex-wrap">
                    <div className="font-medium text-ink inline-flex items-center gap-2"><HardHat size={15} /> {w.worker_name}</div>
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="bg-cream2 border border-ink/10 rounded-sm px-2.5 py-1" data-testid={`installers-ts-days-${w.worker_id}`}><b>{w.days_worked}</b> gg lavorati · <b>{w.days_worked - (w.half_days || 0)}</b> interi + <b className={w.half_days > 0 ? "text-orange-700" : ""}>{w.half_days || 0}</b> mezze gg</span>
                      <span className="bg-cream2 border border-ink/10 rounded-sm px-2.5 py-1" data-testid={`installers-ts-hours-${w.worker_id}`}>Ore: <b>{fmtMinutes(w.total_minutes)}</b></span>
                      <span className={`border rounded-sm px-2.5 py-1 ${(w.overtime_net_minutes ?? w.overtime_minutes) > 0 ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-cream2 border-ink/10"}`} title={`Straordinario lordo: ${fmtMinutes(w.overtime_minutes)} · Ore mancanti compensate: ${fmtMinutes(Math.min(w.overtime_minutes, w.deficit_minutes || 0))}`} data-testid={`installers-ts-overtime-${w.worker_id}`}>Straordinario: <b>{fmtMinutes(w.overtime_net_minutes ?? w.overtime_minutes)}</b></span>
                      {(w.permit_minutes || 0) > 0 && (
                        <span className="border rounded-sm px-2.5 py-1 bg-rose-50 border-rose-200 text-rose-800" title={`Ore mancanti non coperte dagli straordinari${(w.permesso_days || 0) > 0 ? ` + ${w.permesso_days} giornate intere senza ingresso entro le 14:00` : ""}`} data-testid={`installers-ts-permits-${w.worker_id}`}>Permessi: <b>{fmtMinutes(w.permit_minutes)}</b>{(w.permesso_days || 0) > 0 && <> · <b>{w.permesso_days}</b> {w.permesso_days === 1 ? "g intero" : "gg interi"}</>}</span>
                      )}
                      {(w.absence_days || 0) > 0 && (
                        <span className="border rounded-sm px-2.5 py-1 bg-sky-50 border-sky-200 text-sky-800" title="Giornate non lavorate giustificate (malattia/ferie)" data-testid={`installers-ts-absences-${w.worker_id}`}>Assenze: <b>{w.absence_days}</b>{(w.ferie_days || 0) > 0 || (w.malattia_days || 0) > 0 ? <> · <b>{w.ferie_days || 0}</b> ferie · <b>{w.malattia_days || 0}</b> mal.</> : null}</span>
                      )}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <button
                        onClick={() => { setAbsAdd({ workerId: w.worker_id, workerName: w.worker_name }); setAbsDate(""); setAbsType("ferie"); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-sky-300 text-sky-800 bg-sky-50 hover:bg-sky-100 text-[10px] tracking-wider uppercase rounded-sm font-bold transition-colors"
                        data-testid={`installers-ts-add-absence-${w.worker_id}`}
                      >
                        <Plus size={12} /> Assenza
                      </button>
                      <a
                        href={`${API}/installers/admin/timesheets/month/${w.worker_id}/pdf?month=${timesheets.month}&token=${localStorage.getItem("ah_token") || ""}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-cream text-[10px] tracking-wider uppercase rounded-sm font-bold"
                        data-testid={`installers-ts-pdf-${w.worker_id}`}
                      >
                        <FileDown size={12} /> PDF mese
                      </a>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-slate1 border-b border-ink/10">
                        <th className="px-4 py-2.5">Giorno</th>
                        <th className="px-4 py-2.5">Timbrature</th>
                        <th className="px-4 py-2.5 text-right">Ore</th>
                        <th className="px-4 py-2.5 text-right">Straord.</th>
                        <th className="px-2 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.days.map((d) => {
                        const firstIn = d.entries.find((e) => e.type === "in");
                        const lastOut = [...d.entries].reverse().find((e) => e.type === "out");
                        return (
                          <tr key={d.date} className={`border-b border-ink/5 ${d.absence ? "bg-sky-50/50" : d.permesso ? "bg-violet-50/40" : d.auto_closed ? "bg-rose-50/40" : ""}`} data-testid={`installers-ts-day-${w.worker_id}-${d.date}`}>
                            <td className={`px-4 py-2 capitalize whitespace-nowrap ${d.absence ? "text-sky-700 font-semibold" : d.permesso ? "text-violet-700 font-semibold" : d.auto_closed ? "text-rose-600 font-semibold" : "text-ink"}`}>
                              {new Date(`${d.date}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" })}
                            </td>
                            <td className="px-4 py-2 text-xs text-slate-600">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {d.entries.map((e) => {
                                  const hasGeo = e.lat != null && e.lng != null;
                                  const far = e.far_from_site;
                                  const noQr = !e.qr_code && !e.site_id && !e.manual && !e.auto_closed;
                                  let loc = "";
                                  if (far) loc = `${(e.distance_from_site_m / 1000).toFixed(1)} km da ${e.site_name || "sede"}`;
                                  else if (e.site_name) loc = e.site_name;
                                  else if (e.address) loc = e.address.length > 28 ? `${e.address.slice(0, 28)}…` : e.address;
                                  const chip = (
                                    <span
                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-[10px] leading-none ${far ? "bg-rose-50 border-rose-300 text-rose-700 font-bold" : "bg-cream2 border-ink/10 text-slate-600"}`}
                                      title={`${e.address || ""}${e.distance_from_site_m != null ? ` · ${e.distance_from_site_m} m dalla sede` : ""}${e.note ? ` · Giustificazione: ${e.note}` : ""}`}
                                      data-testid={`ts-entry-${e.id}`}
                                    >
                                      <b className={e.type === "in" ? "text-emerald-700" : "text-slate-800"}>{e.type === "in" ? "IN" : "OUT"}</b>
                                      {fmtTs(e.timestamp)}
                                      {hasGeo && <MapPin size={9} className={far ? "text-rose-600" : "text-slate-400"} />}
                                      {loc && <span className="max-w-[190px] truncate">{loc}</span>}
                                      {noQr && <span className="text-[8px] tracking-wider uppercase font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-0.5 leading-none" data-testid={`ts-entry-noqr-${e.id}`}>Senza QR</span>}
                                      {e.note && <span className="italic text-slate-500 max-w-[170px] truncate" data-testid={`ts-entry-note-${e.id}`}>“{e.note}”</span>}
                                    </span>
                                  );
                                  return (
                                    <span key={e.id} className="inline-flex items-center gap-0.5">
                                      {hasGeo ? (
                                        <a href={`https://maps.google.com/?q=${e.lat},${e.lng}`} target="_blank" rel="noreferrer" className="hover:opacity-75" data-testid={`ts-entry-map-${e.id}`}>{chip}</a>
                                      ) : chip}
                                      <button onClick={() => setEntryModal({ id: e.id, worker_id: w.worker_id, worker_name: w.worker_name, date: d.date, type: e.type, time: fmtTs(e.timestamp), note: e.note || "", isNew: false })} className="p-0.5 text-slate-300 hover:text-ink rounded-sm" title="Modifica timbratura" data-testid={`ts-entry-edit-${e.id}`}><Pencil size={11} /></button>
                                      <button onClick={() => deleteTimbratura(e.id)} className="p-0.5 text-slate-300 hover:text-rose-600 rounded-sm" title="Elimina timbratura" data-testid={`ts-entry-del-${e.id}`}><X size={11} /></button>
                                    </span>
                                  );
                                })}
                              </div>
                              <span className="inline-flex flex-wrap gap-2 mt-1">
                                {d.absence && <span className="inline-flex items-center gap-1 text-[9px] tracking-wider uppercase font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5" title="Giornata non lavorata giustificata (malattia/ferie)" data-testid={`installers-ts-absence-${w.worker_id}-${d.date}`}>Assente · {d.absence === "malattia" ? "Malattia" : "Ferie"}{d.absence_id && <button onClick={() => deleteAbsence(d.absence_id, d.date)} className="ml-0.5 hover:text-rose-600" title="Rimuovi assenza" data-testid={`installers-ts-absence-del-${w.worker_id}-${d.date}`}><X size={10} /></button>}</span>}
                                {d.permesso && <span className="text-[9px] tracking-wider uppercase font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5" title="Nessun ingresso timbrato entro le 14:00 → 9h di permesso" data-testid={`installers-ts-permesso-${w.worker_id}-${d.date}`}>Permesso 9h</span>}
                                {d.auto_closed && <span className="text-[9px] tracking-wider uppercase font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5" data-testid={`installers-ts-auto-${w.worker_id}-${d.date}`}>Uscita auto 17:00</span>}
                                {d.half_day && <span className="text-[9px] tracking-wider uppercase font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5" data-testid={`installers-ts-half-${w.worker_id}-${d.date}`}>Mezza giornata</span>}
                                {d.manual && <span className="text-[9px] tracking-wider uppercase font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5" data-testid={`installers-ts-manual-${w.worker_id}-${d.date}`}>Modificata</span>}
                              </span>
                            </td>
                            <td className={`px-4 py-2 text-right font-medium ${d.half_day ? "text-orange-700" : ""}`}>{(d.permesso || d.absence) && !d.minutes ? "—" : <>{fmtMinutes(d.minutes)}{d.half_day ? " (½)" : ""}</>}</td>
                            <td className={`px-4 py-2 text-right ${d.overtime_minutes > 0 ? "text-amber-700 font-semibold" : d.deficit_minutes > 0 ? "text-rose-600 font-semibold" : "text-slate-300"}`} title={d.deficit_minutes > 0 ? "Ore mancanti rispetto alle 9h: compensate con gli straordinari del mese o segnate come permesso" : undefined}>
                              {d.overtime_minutes > 0 ? fmtMinutes(d.overtime_minutes) : d.deficit_minutes > 0 ? `−${fmtMinutes(d.deficit_minutes)}` : "—"}
                            </td>
                            <td className="px-2 py-2 text-right whitespace-nowrap">
                              <button
                                onClick={() => setEntryModal({ id: null, worker_id: w.worker_id, worker_name: w.worker_name, date: d.date, type: "in", time: "08:00", note: "", isNew: true })}
                                className="p-1.5 text-slate1 hover:text-emerald-700 rounded-sm hover:bg-ink/5" title="Aggiungi timbratura a questo giorno"
                                data-testid={`installers-ts-addentry-${w.worker_id}-${d.date}`}
                              ><Plus size={13} /></button>
                              <button
                                onClick={() => setTsEdit({ worker_id: w.worker_id, worker_name: w.worker_name, date: d.date, in_time: firstIn ? fmtTs(firstIn.timestamp) : "08:00", out_time: lastOut ? fmtTs(lastOut.timestamp) : "", isNew: false })}
                                className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Modifica entrata/uscita (giornata)"
                                data-testid={`installers-ts-edit-${w.worker_id}-${d.date}`}
                              ><Pencil size={13} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------------- ASSISTENZE ---------------- */}
      {sub === "assistenze" && (
        <InstallerAssistenze
          reloadKey={assistReload}
          onNew={() => { setDupFrom(null); setNewTaskType("assistance"); setModalTask(null); }}
          onEdit={(t) => { setDupFrom(null); setModalTask(t); }}
        />
      )}

      {/* ---------------- CONTESTAZIONI ---------------- */}
      {sub === "contestazioni" && (
        <InstallerContestazioni
          reloadKey={assistReload}
          onOpenTask={(t) => { setDupFrom(null); setModalTask(t); }}
        />
      )}

      {/* ---------------- ATTIVITÀ ---------------- */}
      {sub === "attivita" && (
        <InstallerAttivita
          reloadKey={actReload}
          onNew={() => setModalActivity(null)}
          onEdit={(t) => setModalActivity(t)}
          onOpenLinked={openLinkedTask}
        />
      )}

      {/* ---------------- BUSTE PAGA ---------------- */}
      {sub === "operai" && operaiTab === "bustepaga" && <div className="mt-1"><InstallerPayslips workers={workers} /></div>}

      {/* ---------------- STATISTICHE ---------------- */}
      {sub === "statistiche" && <InstallerStats onOpenTask={(t) => { setDupFrom(null); setModalTask(t); }} />}

      {/* ---------------- RUBRICA CLIENTI ---------------- */}
      {sub === "rubrica" && <InstallerRubrica onOpenTask={(t) => { setDupFrom(null); setModalTask(t); }} />}

      {/* ---------------- SEDI / CANTIERI (QR timbratura) ---------------- */}
      {sub === "operai" && operaiTab === "sedi" && (
        <div className="space-y-4" data-testid="installers-sedi">
          <form onSubmit={createSite} className="bg-white border border-ink/10 rounded-sm p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Nome sede *</label>
              <input required value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="Sede Milano" className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="site-form-name" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Indirizzo</label>
              <input value={siteForm.address} onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })} placeholder="Via Roma 1" className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="site-form-address" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Lat</label>
                <input value={siteForm.lat} onChange={(e) => setSiteForm({ ...siteForm, lat: e.target.value })} placeholder="45.46" className="mt-1 w-full border border-ink/20 px-2 py-2.5 rounded-sm bg-white text-sm" data-testid="site-form-lat" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Lng</label>
                <input value={siteForm.lng} onChange={(e) => setSiteForm({ ...siteForm, lng: e.target.value })} placeholder="9.19" className="mt-1 w-full border border-ink/20 px-2 py-2.5 rounded-sm bg-white text-sm" data-testid="site-form-lng" />
              </div>
            </div>
            <button type="submit" className="inline-flex items-center justify-center gap-2 bg-ink text-cream px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="site-form-save">
              <Plus size={14} /> Crea sede
            </button>
          </form>
          <div className="text-[11px] text-slate1">Stampa il QR e appendilo in sede/cantiere: gli operai lo scansionano dall&apos;app per timbrare entrata e uscita.</div>

          {sites.length === 0 ? (
            <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="installers-no-sites">Nessuna sede creata</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sites.map((s) => (
                <div key={s.id} className="bg-white border border-ink/10 rounded-sm p-4 flex gap-4" data-testid={`installers-site-${s.id}`}>
                  <div className="bg-white border border-ink/10 rounded-sm p-2 self-start">
                    <QRCodeSVG id={`site-qr-${s.id}`} value={s.qr_token} size={96} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink flex items-center gap-1.5"><MapPin size={13} className="shrink-0 text-slate1" /> <span className="truncate">{s.name}</span></div>
                    {s.address && <div className="text-xs text-slate1 mt-0.5 truncate">{s.address}</div>}
                    {(s.lat != null && s.lng != null) && <div className="text-[10px] text-slate-400 mt-0.5">{s.lat}, {s.lng}</div>}
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => printSiteQr(s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink text-cream text-[10px] tracking-wider uppercase rounded-sm font-bold" data-testid={`site-print-${s.id}`}>
                        <Printer size={11} /> Stampa QR
                      </button>
                      <button onClick={() => deleteSite(s)} className="p-1.5 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" title="Elimina sede" data-testid={`site-del-${s.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {wEdit && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="worker-edit-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setWEdit(null); }}>
          <div className="bg-cream w-[min(94vw,440px)] border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between">
              <div className="font-display text-lg text-ink">Modifica operaio</div>
              <button onClick={() => setWEdit(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="worker-edit-close"><X size={18} /></button>
            </div>
            <form onSubmit={saveWorkerEdit} className="p-6 space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Nome e cognome *</label>
                <input required value={wEdit.name} onChange={(e) => setWEdit({ ...wEdit, name: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="worker-edit-name" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Email (login app) *</label>
                <input required type="email" value={wEdit.email} onChange={(e) => setWEdit({ ...wEdit, email: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="worker-edit-email" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Telefono</label>
                <input value={wEdit.phone} onChange={(e) => setWEdit({ ...wEdit, phone: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="worker-edit-phone" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Qualifica</label>
                <select value={wEdit.qualifica || "operaio"} onChange={(e) => setWEdit({ ...wEdit, qualifica: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="worker-edit-qualifica">
                  <option value="operaio">Operaio</option>
                  <option value="responsabile">Responsabile</option>
                </select>
                {wEdit.qualifica === "responsabile" && <p className="mt-1 text-[10px] text-indigo-800">Vede tutte le consegne (con foto e assegnazioni) e gestisce la ferramenta. Non può creare, modificare, assegnare o cancellare consegne.</p>}
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Password attuale</label>
                {wEdit.password_admin ? (
                  <div className="mt-1 flex items-center gap-2" data-testid="worker-edit-current-pwd">
                    <input
                      readOnly
                      type={wEdit.showPwd ? "text" : "password"}
                      value={wEdit.password_admin}
                      className="flex-1 border border-ink/15 px-3 py-2.5 rounded-sm bg-cream2/60 text-sm font-mono"
                      data-testid="worker-edit-current-pwd-input"
                    />
                    <button type="button" onClick={() => setWEdit({ ...wEdit, showPwd: !wEdit.showPwd })} className="p-2.5 border border-ink/15 rounded-sm text-slate1 hover:text-ink bg-white" title={wEdit.showPwd ? "Nascondi" : "Mostra"} data-testid="worker-edit-pwd-toggle">
                      {wEdit.showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <button type="button" onClick={() => { navigator.clipboard.writeText(wEdit.password_admin); toast.success("Password copiata"); }} className="p-2.5 border border-ink/15 rounded-sm text-slate1 hover:text-ink bg-white" title="Copia password" data-testid="worker-edit-pwd-copy">
                      <Copy size={15} />
                    </button>
                  </div>
                ) : (
                  <p className="mt-1 text-[11px] text-slate1 bg-cream2/60 border border-ink/10 rounded-sm px-3 py-2.5" data-testid="worker-edit-pwd-unknown">
                    Impostata dall&apos;operaio alla registrazione: non visibile. Puoi assegnarne una nuova qui sotto.
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Nuova password</label>
                <input type="password" autoComplete="new-password" placeholder="Lascia vuoto per non cambiarla" value={wEdit.password} onChange={(e) => setWEdit({ ...wEdit, password: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="worker-edit-password" />
                <p className="text-[11px] text-slate1 mt-1">Min 6 caratteri. L&apos;operaio userà la nuova password al prossimo login sull&apos;app.</p>
              </div>
              <button type="submit" className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="worker-edit-save">Salva modifiche</button>
            </form>
          </div>
        </div>
      )}

      {wDelete && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="worker-delete-modal" onClick={(e) => { if (e.target === e.currentTarget) setWDelete(null); }}>
          <div className="bg-cream w-[min(94vw,460px)] border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between">
              <div className="font-display text-lg text-rose-700">Elimina operaio</div>
              <button onClick={() => setWDelete(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="worker-delete-close"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {wDelStep === 1 ? (
                <>
                  <p className="text-sm text-ink">Stai per eliminare <b>{wDelete.name}</b> ({wDelete.email}).</p>
                  <p className="text-sm text-slate-600">L&apos;operaio non potrà più accedere all&apos;app e le sue consegne resteranno senza assegnatario. Le timbrature registrate restano nello storico. <b>L&apos;operazione non è reversibile.</b></p>
                  <div className="flex gap-3">
                    <button onClick={() => setWDelete(null)} className="flex-1 border border-ink/20 py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm text-ink" data-testid="worker-delete-cancel">Annulla</button>
                    <button onClick={() => setWDelStep(2)} className="flex-1 bg-rose-600 text-white py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm font-bold" data-testid="worker-delete-continue">Continua</button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-ink">Seconda conferma: scrivi il nome dell&apos;operaio <b className="text-rose-700">{wDelete.name}</b> per confermare l&apos;eliminazione definitiva.</p>
                  <input autoFocus value={wDelText} onChange={(e) => setWDelText(e.target.value)} placeholder={wDelete.name} className="w-full border border-rose-300 px-3 py-2.5 rounded-sm bg-white" data-testid="worker-delete-confirm-input" />
                  <div className="flex gap-3">
                    <button onClick={() => setWDelete(null)} className="flex-1 border border-ink/20 py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm text-ink" data-testid="worker-delete-cancel-2">Annulla</button>
                    <button
                      onClick={deleteWorker}
                      disabled={wDelText.trim().toLowerCase() !== (wDelete.name || "").trim().toLowerCase()}
                      className="flex-1 bg-rose-600 text-white py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="worker-delete-final"
                    >Elimina definitivamente</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {absAdd && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="ts-absence-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setAbsAdd(null); }}>
          <form onSubmit={submitAbsence} className="bg-cream w-[min(94vw,420px)] border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between">
              <div className="font-display text-lg text-ink">Assenza — {absAdd.workerName}</div>
              <button type="button" onClick={() => setAbsAdd(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="ts-absence-close"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Data *</label>
                <input type="date" required value={absDate} onChange={(e) => setAbsDate(e.target.value)} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-absence-date" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1 block mb-1">Tipo *</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["ferie", "Ferie"], ["malattia", "Malattia"]].map(([val, lab]) => (
                    <button type="button" key={val} onClick={() => setAbsType(val)} className={`px-3 py-2.5 rounded-sm border text-[11px] tracking-wider uppercase font-bold transition-colors ${absType === val ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate1 border-ink/15 hover:text-ink"}`} data-testid={`ts-absence-type-${val}`}>{lab}</button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-slate1">La giornata verrà conteggiata come non lavorata giustificata (non come permesso). Se esiste già un'assenza in quella data, verrà sostituita.</p>
              <button type="submit" disabled={absBusy} className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="ts-absence-save">
                {absBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Registra assenza
              </button>
            </div>
          </form>
        </div>
      )}

      {absEdit && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="abs-edit-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setAbsEdit(null); }}>
          <div className="bg-cream w-[min(94vw,420px)] border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between">
              <div className="font-display text-lg text-ink">Modifica assenza — {absEdit.worker_name}</div>
              <button type="button" onClick={() => setAbsEdit(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="abs-edit-close"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Data *</label>
                <input type="date" value={absEdit.date} onChange={(e) => setAbsEdit({ ...absEdit, date: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="abs-edit-date" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1 block mb-1">Tipo *</label>
                <div className="grid grid-cols-3 gap-2">
                  {[["ferie", "Ferie"], ["malattia", "Malattia"], ["permesso", "Permesso"]].map(([val, lab]) => (
                    <button type="button" key={val} onClick={() => setAbsEdit({ ...absEdit, type: val })} className={`px-2 py-2.5 rounded-sm border text-[11px] tracking-wider uppercase font-bold transition-colors ${absEdit.type === val ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate1 border-ink/15 hover:text-ink"}`} data-testid={`abs-edit-type-${val}`}>{lab}</button>
                  ))}
                </div>
              </div>
              <button onClick={saveAbsEdit} disabled={absBusyId === absEdit.id} className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="abs-edit-save">
                {absBusyId === absEdit.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {tsEdit && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="ts-edit-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setTsEdit(null); }}>
          <div className="bg-cream w-[min(94vw,440px)] border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between">
              <div className="font-display text-lg text-ink">
                {tsEdit.isNew ? "Aggiungi giorno" : `Modifica orari — ${tsEdit.worker_name}`}
              </div>
              <button onClick={() => setTsEdit(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="ts-edit-close"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {tsEdit.isNew && (
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Operaio *</label>
                  <select value={tsEdit.worker_id} onChange={(e) => setTsEdit({ ...tsEdit, worker_id: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-edit-worker">
                    <option value="">Seleziona operaio…</option>
                    {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Data *</label>
                <input type="date" disabled={!tsEdit.isNew} value={tsEdit.date} onChange={(e) => setTsEdit({ ...tsEdit, date: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white disabled:opacity-60" data-testid="ts-edit-date" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Entrata *</label>
                  <input type="time" value={tsEdit.in_time} onChange={(e) => setTsEdit({ ...tsEdit, in_time: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-edit-in" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Uscita</label>
                  <input type="time" value={tsEdit.out_time} onChange={(e) => setTsEdit({ ...tsEdit, out_time: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-edit-out" />
                </div>
              </div>
              <p className="text-[11px] text-slate1" data-testid="ts-edit-hint">
                {tsEdit.out_time
                  ? "Gli orari inseriti sostituiscono tutte le timbrature del giorno (anche l'uscita automatica)."
                  : "Uscita vuota: viene salvata solo l'entrata — l'uscita la timbra l'operaio dall'app (o la chiusura automatica serale)."}
              </p>
              <button onClick={saveTsEdit} className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="ts-edit-save">
                {tsEdit.isNew ? "Aggiungi giorno" : "Salva orari"}
              </button>
              {!tsEdit.isNew && (
                <button
                  onClick={deleteTsDay}
                  className="w-full inline-flex items-center justify-center gap-2 border border-rose-300 text-rose-700 hover:bg-rose-50 py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm font-bold"
                  data-testid="ts-edit-delete-day"
                >
                  <Trash2 size={13} /> Elimina tutte le timbrature del giorno
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {entryModal && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="ts-entry-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setEntryModal(null); }}>
          <div className="bg-cream w-full max-w-md rounded-sm shadow-2xl">
            <div className="p-5 border-b border-ink/10 flex items-center justify-between">
              <div className="font-display text-lg text-ink">{entryModal.isNew ? "Aggiungi timbratura" : "Modifica timbratura"}</div>
              <button onClick={() => setEntryModal(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="ts-entry-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {entryModal.isNew ? (
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Operaio *</label>
                  <select value={entryModal.worker_id} onChange={(e) => { const w = workers.find((x) => x.id === e.target.value); setEntryModal({ ...entryModal, worker_id: e.target.value, worker_name: w ? w.name : "" }); }} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-entry-worker">
                    <option value="">Seleziona operaio…</option>
                    {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="text-sm text-ink"><span className="text-slate1">Operaio:</span> <b>{entryModal.worker_name}</b></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Giorno *</label>
                  <input type="date" value={entryModal.date} onChange={(e) => setEntryModal({ ...entryModal, date: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-entry-date" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Orario *</label>
                  <input type="time" value={entryModal.time} onChange={(e) => setEntryModal({ ...entryModal, time: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-entry-time" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Tipo *</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setEntryModal({ ...entryModal, type: "in" })} className={`py-2.5 rounded-sm text-[11px] tracking-wider uppercase font-bold border transition-colors ${entryModal.type === "in" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate1 border-ink/20"}`} data-testid="ts-entry-type-in">Entrata</button>
                  <button type="button" onClick={() => setEntryModal({ ...entryModal, type: "out" })} className={`py-2.5 rounded-sm text-[11px] tracking-wider uppercase font-bold border transition-colors ${entryModal.type === "out" ? "bg-ink text-cream border-ink" : "bg-white text-slate1 border-ink/20"}`} data-testid="ts-entry-type-out">Uscita</button>
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase text-slate1">Nota (facoltativa)</label>
                <input type="text" value={entryModal.note} onChange={(e) => setEntryModal({ ...entryModal, note: e.target.value })} placeholder="Es. correzione amministrativa" className="mt-1 w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="ts-entry-note" />
              </div>
              <button onClick={saveEntryModal} className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="ts-entry-save">
                {entryModal.isNew ? "Aggiungi timbratura" : "Salva modifiche"}
              </button>
              {!entryModal.isNew && (
                <button onClick={() => { const id = entryModal.id; setEntryModal(null); deleteTimbratura(id); }} className="w-full inline-flex items-center justify-center gap-2 border border-rose-300 text-rose-700 hover:bg-rose-50 py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm font-bold" data-testid="ts-entry-delete">
                  <Trash2 size={13} /> Elimina questa timbratura
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {modalTask !== undefined && (
        <InstallerTaskModal
          task={modalTask}
          workers={workers}
          duplicateFrom={dupFrom}
          defaultType={newTaskType}
          defaultDate={newTaskDate || ((isoDay(new Date()) >= isoDay(weekStart) && isoDay(new Date()) <= isoDay(addDays(weekStart, 6))) ? isoDay(new Date()) : isoDay(weekStart))}
          onClose={() => { setModalTask(undefined); setDupFrom(null); setNewTaskDate(null); setNewTaskType("delivery"); }}
          onSaved={() => { setModalTask(undefined); setDupFrom(null); setNewTaskDate(null); setNewTaskType("delivery"); setAssistReload((k) => k + 1); loadTasks(); loadBase(); }}
        />
      )}
      {modalActivity !== undefined && (
        <ActivityModal
          task={modalActivity}
          workers={workers}
          defaultDate={newActivityDate || ((isoDay(new Date()) >= isoDay(weekStart) && isoDay(new Date()) <= isoDay(addDays(weekStart, 6))) ? isoDay(new Date()) : isoDay(weekStart))}
          onClose={() => { setModalActivity(undefined); setNewActivityDate(null); }}
          onSaved={() => { setModalActivity(undefined); setNewActivityDate(null); setActReload((k) => k + 1); loadTasks(); loadBase(); }}
        />
      )}
    </div>
  );
}
